import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl, InputLabel,
  MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import bookingService from '../../services/bookingService';

/** The value the "every booking flow" row uses, since a real flow id is never empty. */
const EVERY_FLOW = '';

const PROVIDERS = [
  { id: 'NATIVE', label: 'FlowGate’s own diary' },
  { id: 'CAL_COM', label: 'Cal.com' },
];

const providerLabel = (id) => PROVIDERS.find((p) => p.id === id)?.label || id;

/**
 * Settings → Booking → where each booking flow books.
 *
 * <p>One row per binding. A binding with no flow is the default every booking flow uses; a binding
 * naming a flow overrides it. No binding at all means FlowGate's own diary, which is why an
 * untouched account shows nothing here and behaves exactly as it did.
 *
 * <p>The API key is write-only, it is never returned, so the field is always empty and leaving it
 * empty on an edit keeps the stored one. That is worth saying on screen, because an empty password
 * box next to a working connection otherwise reads as "not configured".
 */
const BookingProvidersPanel = ({ bookingFlows = [] }) => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;

  const [bindings, setBindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [flowId, setFlowId] = useState(EVERY_FLOW);
  const [provider, setProvider] = useState('CAL_COM');
  const [apiKey, setApiKey] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const load = useCallback(async () => {
    if (!envId) return;
    setLoading(true);
    setError(null);
    try {
      setBindings(await bookingService.listProviders(envId, authenticatedFetch) || []);
    } catch (e) {
      setError(e.message || 'Failed to read the booking providers');
    } finally {
      setLoading(false);
    }
    // authenticatedFetch is recreated every render; a dependency here would poll in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envId]);

  useEffect(() => { load(); }, [load]);

  const readEventTypes = async () => {
    setLoadingTypes(true);
    setError(null);
    try {
      const types = await bookingService.calComEventTypes(
        envId, { flowId: flowId || undefined, apiKey: apiKey || undefined }, authenticatedFetch);
      setEventTypes(types || []);
      if (!types?.length) {
        setError('That key works, but the account has no event types to book.');
      }
    } catch (e) {
      setError(e.message || 'Could not read your Cal.com calendars');
    } finally {
      setLoadingTypes(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await bookingService.saveProvider(envId, {
        flowId: flowId || null,
        provider,
        apiKey: apiKey || null,
        eventTypes: chosen,
        timeZone: timeZone || null,
      }, authenticatedFetch);
      setApiKey('');
      setChosen([]);
      setEventTypes([]);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to save where this flow books');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    setError(null);
    try {
      await bookingService.deleteProvider(envId, id, authenticatedFetch);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const nameOf = (id) => bookingFlows.find((flow) => flow.id === id)?.name || 'a flow';

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Where bookings go</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each booking flow can book into a different diary. Anything not listed here uses
          FlowGate’s own.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}
                 data-testid="booking-providers-error">
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={1} sx={{ mb: 3 }}>
            {bindings.length === 0 && !error && (
              <Typography variant="body2" color="text.secondary">
                Every booking flow uses FlowGate’s own diary.
              </Typography>
            )}
            {bindings.map((binding) => (
              <Stack
                key={binding.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                data-testid={`booking-provider-${binding.id}`}
              >
                <Box>
                  <Typography variant="body2">
                    {binding.flowId ? nameOf(binding.flowId) : 'Every booking flow'}
                    {' → '}
                    <b>{providerLabel(binding.provider)}</b>
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    {binding.keySet && <Chip size="small" label="Key set" />}
                    {binding.timeZone && <Chip size="small" variant="outlined" label={binding.timeZone} />}
                    {binding.eventTypes?.length > 0 && (
                      <Chip size="small" variant="outlined"
                            label={`${binding.eventTypes.length} calendar${binding.eventTypes.length === 1 ? '' : 's'}`} />
                    )}
                  </Stack>
                </Box>
                <Button size="small" color="error" onClick={() => remove(binding.id)}
                        data-testid={`remove-booking-provider-${binding.id}`}>
                  Remove
                </Button>
              </Stack>
            ))}
          </Stack>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Send a flow somewhere else</Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="booking-provider-flow">Which flow</InputLabel>
            <Select
              labelId="booking-provider-flow"
              label="Which flow"
              value={flowId}
              onChange={(e) => setFlowId(e.target.value)}
              inputProps={{ 'data-testid': 'booking-provider-flow-select' }}
            >
              <MenuItem value={EVERY_FLOW}>Every booking flow</MenuItem>
              {bookingFlows.map((flow) => (
                <MenuItem key={flow.id} value={flow.id}>{flow.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="booking-provider-kind">Books into</InputLabel>
            <Select
              labelId="booking-provider-kind"
              label="Books into"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              inputProps={{ 'data-testid': 'booking-provider-kind-select' }}
            >
              {PROVIDERS.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {provider === 'CAL_COM' && (
            <>
              <Alert severity="info">
                Cal.com needs the event type to be <b>phone-only</b>. A WhatsApp booking has a phone
                number and no email address, and an ordinary Cal.com event type requires one.
              </Alert>
              <TextField
                fullWidth
                size="small"
                type="password"
                label="Cal.com API key"
                placeholder="Leave empty to keep the key already saved"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                helperText="Cal.com → Settings → Developer → API keys. Available on the free plan."
                inputProps={{ 'data-testid': 'booking-provider-api-key' }}
              />
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={readEventTypes}
                  disabled={loadingTypes}
                  data-testid="load-calcom-event-types"
                >
                  {loadingTypes ? 'Reading your calendars…' : 'Read my calendars'}
                </Button>
              </Box>
              {eventTypes.length > 0 && (
                <FormControl fullWidth size="small">
                  <InputLabel id="booking-provider-event-types">Calendars to offer</InputLabel>
                  <Select
                    labelId="booking-provider-event-types"
                    label="Calendars to offer"
                    multiple
                    value={chosen}
                    onChange={(e) => setChosen(e.target.value)}
                    inputProps={{ 'data-testid': 'booking-provider-event-types-select' }}
                  >
                    {eventTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.title}
                        {type.lengthInMinutes ? ` · ${type.lengthInMinutes} min` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Leave none selected to offer all of them.
                  </Typography>
                </FormControl>
              )}
              <TextField
                fullWidth
                size="small"
                label="Time zone"
                placeholder="Europe/London"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                inputProps={{ 'data-testid': 'booking-provider-timezone' }}
              />
            </>
          )}

          <Box>
            <Button variant="contained" onClick={save} disabled={saving}
                    data-testid="save-booking-provider">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default BookingProvidersPanel;
